(() => {
  if (window.PipWellsSecondRoomCartHandlerLoaded) return;
  window.PipWellsSecondRoomCartHandlerLoaded = true;

  const getRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const addCalendarDays = (date, days) => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    result.setDate(result.getDate() + Number(days || 0));
    return result;
  };

  const europeanDeliveryCountries = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
  ]);

  const deliveryDaysForCountry = (country) => {
    if (country === 'NL') return { min: 1, max: 1 };
    if (country === 'BE') return { min: 1, max: 2 };
    if (country === 'DE' || country === 'FR') return { min: 2, max: 3 };
    if (country === 'GB') return { min: 3, max: 5 };
    if (europeanDeliveryCountries.has(country)) return { min: 2, max: 4 };
    return null;
  };

  const isBeforeCutoff = (date = new Date()) => {
    const hour = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam',
      hour: '2-digit',
      hourCycle: 'h23'
    }).format(date);
    return Number(hour) < 23;
  };

  const deliveryDates = (startDate, endDate, locale) => {
    const date = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
    const start = date.format(startDate);
    const end = date.format(endDate);
    return start === end ? start : `${start} – ${end}`;
  };

  let detectedCountryPromise = null;
  const detectVisitorCountry = () => {
    if (detectedCountryPromise) return detectedCountryPromise;

    const endpoint = new URL(getRoot() + 'browsing_context_suggestions.json', window.location.origin);
    endpoint.searchParams.set('country[enabled]', 'true');

    detectedCountryPromise = fetch(endpoint.toString(), { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => payload && payload.detected_values && payload.detected_values.country
        ? payload.detected_values.country.handle
        : null)
      .catch(() => null);

    return detectedCountryPromise;
  };

  const updateDeliveryEstimates = async () => {
    const estimates = Array.from(document.querySelectorAll('[data-pw-delivery-estimate]'));

    for (const estimate of estimates) {
      const output = estimate.querySelector('[data-pw-delivery-text]');
      const note = estimate.querySelector('[data-pw-delivery-note]');
      if (!output) continue;

      const locale = estimate.dataset.locale || document.documentElement.lang || 'en';
      let country = String(estimate.dataset.country || '').toUpperCase();
      const now = new Date();

      // The international .com market has an ambiguous AT default. Keep the block hidden until
      // Shopify resolves the visitor country, then show only a range supported by the
      // current Shipping & Delivery policy.
      if (estimate.dataset.detectCountry === 'true') {
        country = String(await detectVisitorCountry() || '').toUpperCase();
        if (!deliveryDaysForCountry(country)) {
          estimate.dataset.resolvedCountry = '';
          estimate.hidden = true;
          continue;
        }
      }

      const deliveryDays = deliveryDaysForCountry(country);
      estimate.dataset.resolvedCountry = country;
      estimate.hidden = !deliveryDays;
      if (!deliveryDays) continue;

      const minDays = deliveryDays.min;
      const maxDays = deliveryDays.max;
      const cutoffPassed = !isBeforeCutoff(now);
      const dispatchDelay = cutoffPassed ? 1 : 0;
      const startDate = addCalendarDays(now, minDays + dispatchDelay);
      const endDate = addCalendarDays(now, maxDays + dispatchDelay);
      const dates = deliveryDates(startDate, endDate, locale);
      output.textContent = estimate.dataset.headingTemplate.replace('__DATES__', dates);

      if (note) {
        if (country === 'NL') {
          note.textContent = cutoffPassed
            ? estimate.dataset.nlAfterCutoffNote
            : estimate.dataset.nlCutoffNote;
        } else {
          note.textContent = cutoffPassed ? estimate.dataset.afterCutoffNote : estimate.dataset.beforeCutoffNote;
        }
      }
    }
  };

  const scheduleDeliveryUpdate = () => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(updateDeliveryEstimates));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateDeliveryEstimates, { once: true });
  } else {
    updateDeliveryEstimates();
  }

  document.addEventListener('shopify:section:load', scheduleDeliveryUpdate);
  document.addEventListener('cart:refresh', scheduleDeliveryUpdate);

  const deliveryObserver = new MutationObserver((records) => {
    const estimateWasAdded = records.some((record) => Array.from(record.addedNodes).some((node) =>
      node.nodeType === 1 && (
        node.matches('[data-pw-delivery-estimate]') ||
        node.querySelector('[data-pw-delivery-estimate]')
      )
    ));
    if (estimateWasAdded) scheduleDeliveryUpdate();
  });
  deliveryObserver.observe(document.documentElement, { childList: true, subtree: true });

  let protectionCorrectionPending = false;

  const refreshAfterProtectionUpdate = () => {
    if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  };

  const changeCartLine = async (id, quantity) => {
    const response = await fetch(getRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity })
    });
    if (!response.ok) throw new Error('Unable to correct shipping protection');
  };

  const validateProtectionCart = async (cart) => {
    if (!cart || !Array.isArray(cart.items) || protectionCorrectionPending) return false;

    const protectionVariantId = Number(
      (document.querySelector('[data-pw-shipping-protection]') || {}).dataset?.variantId || 0
    );
    const isProtectionLine = (item) =>
      item.product_handle === 'shipping-protection' ||
      item.handle === 'shipping-protection' ||
      (protectionVariantId > 0 && Number(item.variant_id) === protectionVariantId);
    const protectionLine = cart.items.find(isProtectionLine);
    if (!protectionLine) return false;

    const regularItemCount = cart.items
      .filter((item) => !isProtectionLine(item))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
    const desiredQuantity = regularItemCount === 0 ? 0 : 1;
    if (Number(protectionLine.quantity) === desiredQuantity) return false;

    protectionCorrectionPending = true;
    try {
      await changeCartLine(protectionLine.key || protectionLine.variant_id, desiredQuantity);
      return true;
    } finally {
      protectionCorrectionPending = false;
    }
  };

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart) return;
    validateProtectionCart(cart)
      .then((corrected) => {
        if (corrected) refreshAfterProtectionUpdate();
      })
      .catch(() => {});
  });

  let protectionValidationPending = false;
  const validateExistingProtectionCart = () => {
    if (protectionValidationPending || !document.querySelector('[data-pw-shipping-protection]')) return;

    protectionValidationPending = true;
    fetch(getRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then((cart) => validateProtectionCart(cart))
      .then((corrected) => {
        if (corrected) refreshAfterProtectionUpdate();
      })
      .catch(() => {})
      .finally(() => {
        protectionValidationPending = false;
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', validateExistingProtectionCart, { once: true });
  } else {
    validateExistingProtectionCart();
  }
  document.addEventListener('shopify:section:load', validateExistingProtectionCart);
  document.addEventListener('cart:refresh', validateExistingProtectionCart);

  document.addEventListener('change', async (event) => {
    const checkbox = event.target.closest('.pw-shipping-protection__checkbox');
    if (!checkbox || checkbox.disabled) return;

    const container = checkbox.closest('[data-pw-shipping-protection]');
    if (!container) return;

    const variantId = Number(container.dataset.variantId);
    const lineKey = container.dataset.lineKey;
    const adding = checkbox.checked;
    const status = container.querySelector('.pw-shipping-protection__status');
    let mutationSucceeded = false;

    container.setAttribute('aria-busy', 'true');
    checkbox.disabled = true;
    if (status) status.hidden = true;

    try {
      const endpoint = adding ? 'cart/add.js' : 'cart/change.js';
      const body = adding
        ? { items: [{ id: variantId, quantity: 1, properties: { _pw_shipping_protection: 'true' } }] }
        : { id: lineKey || variantId, quantity: 0 };
      const response = await fetch(getRoot() + endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('Unable to update shipping protection');
      mutationSucceeded = true;

      let cart = null;
      try {
        const cartResponse = await fetch(getRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (error) {
        cart = null;
      }
      if (cart) await validateProtectionCart(cart);
      refreshAfterProtectionUpdate();
    } catch (error) {
      if (mutationSucceeded) {
        refreshAfterProtectionUpdate();
        return;
      }
      checkbox.checked = !adding;
      checkbox.disabled = false;
      container.removeAttribute('aria-busy');
      if (status) status.hidden = false;
    }
  });

  document.documentElement.addEventListener('cart:change', () => {
    scheduleDeliveryUpdate();
  });

  const setMessage = (button, message) => {
    const offer = button.closest('.pw-second-room-offer, .pw-cart-related, .pw-smart-upsell, [data-pw-multi-fragrance]');
    const status = offer && offer.querySelector('[data-pw-second-room-status], [data-pw-cart-smart-status]');
    if (status) status.textContent = message;
  };


  // Matches the theme's own extractSectionId(): the closest ".shopify-section" ancestor's id,
  // with the "shopify-section-" prefix stripped.
  const closestSectionId = (element) => {
    const container = element.closest('.shopify-section');
    return container ? container.id.replace('shopify-section-', '') : null;
  };


  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-pw-upsell-select]');
    if (!select) return;
    const button = select.closest('[data-pw-product-upsell]').querySelector('[data-pw-cart-smart-add]');
    if (button.dataset.pwPending === 'true') return;
    button.disabled = !select.value || select.selectedOptions[0].disabled;
    button.removeAttribute('aria-busy');
    setMessage(button, '');
  });
  const reconcileProductUpsells = (event) => {
    const items = event.detail?.cart?.items;
    if (!Array.isArray(items)) return;
    const ids = new Set(items.map(item => String(item.product_id)));
    document.querySelectorAll('[data-pw-product-upsell] [data-pw-upsell-select]').forEach(select => {
      Array.from(select.options).forEach(option => {
        if (option.dataset.productId) option.disabled = ids.has(option.dataset.productId);
      });
      if (select.selectedOptions[0]?.disabled) select.value = '';
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
    document.querySelectorAll('[data-pw-product-upsell] [data-pw-offer-product]').forEach(card => {
      card.hidden = ids.has(card.dataset.pwOfferProduct);
      card.querySelectorAll('[data-pw-cart-smart-add]').forEach(button => {
        if (button.dataset.pwPending !== 'true') button.disabled = card.hidden;
      });
    });
  };
  document.documentElement.addEventListener('cart:change', reconcileProductUpsells);
  document.documentElement.addEventListener('pw:cart-reconciled', reconcileProductUpsells);

  // Draft choices stay on this storefront until explicitly cleared or added.
  const scentSelectionKey = 'pw-fragrance-selection-v1';
  let scentSelectionFallback = { ids: [], uncertain: [] };
  const validSelectionIds = values => Array.isArray(values)
    ? [...new Set(values.filter(value => typeof value === 'string' && /^\d{1,18}$/.test(value)))].slice(0, 64) : [];
  function readScentSelection() {
    for (const storageName of ['localStorage', 'sessionStorage']) {
      try {
        const raw = window[storageName].getItem(scentSelectionKey);
        if (raw !== null) {
          const saved = JSON.parse(raw);
          return { ids: validSelectionIds(saved.ids), uncertain: validSelectionIds(saved.uncertain) };
        }
      } catch (_) { /* Use the next available storage without blocking shopping. */ }
    }
    return scentSelectionFallback;
  }
  function saveScentSelection(state) {
    scentSelectionFallback = state;
    for (const storageName of ['localStorage', 'sessionStorage']) {
      try { window[storageName].setItem(scentSelectionKey, JSON.stringify(state)); } catch (_) {}
    }
  }
  function refreshScentSelection(multi) {
    const button = multi.querySelector('[data-pw-multi-add]');
    if (!button || button.dataset.pwPending === 'true') return;
    const state = readScentSelection();
    const selected = Array.from(multi.querySelectorAll('[data-pw-scent-choice]:checked:not(:disabled)'));
    const uncertain = selected.some(input => state.uncertain.includes(input.value));
    const count = multi.querySelector('[data-pw-multi-count]');
    if (count) count.textContent = selected.length;
    button.disabled = selected.length === 0 || uncertain;
    if (uncertain) setMessage(button, button.getAttribute('data-pw-error-message') || '');
  }
  function restoreScentSelection() {
    const state = readScentSelection();
    // Migrate existing tab selections. Missing cards must not erase saved choices.
    saveScentSelection(state);
    document.querySelectorAll('[data-pw-multi-fragrance]').forEach(multi => {
      const button = multi.querySelector('[data-pw-multi-add]');
      if (!button || button.dataset.pwPending === 'true') return;
      multi.querySelectorAll('[data-pw-scent-choice]').forEach(input => {
        input.checked = !input.disabled && state.ids.includes(input.value);
      });
      refreshScentSelection(multi);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreScentSelection, { once: true });
  } else restoreScentSelection();
  window.addEventListener('pageshow', restoreScentSelection);
  window.addEventListener('storage', event => {
    if (event.key === scentSelectionKey) restoreScentSelection();
  });
  document.addEventListener('product:rerender', restoreScentSelection);
  document.addEventListener('shopify:section:load', restoreScentSelection);
  const selectionObserver = new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node =>
      node.nodeType === 1 && (node.matches('[data-pw-multi-fragrance], [data-pw-scent-choice]') ||
      node.querySelector('[data-pw-multi-fragrance], [data-pw-scent-choice]'))))) restoreScentSelection();
  });
  selectionObserver.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.matches('[data-pw-scent-choice]') || input.disabled) return;
    const multi = input.closest('[data-pw-multi-fragrance]');
    if (!multi) return;
    const state = readScentSelection();
    state.ids = input.checked ? validSelectionIds([...state.ids, input.value]) : state.ids.filter(id => id !== input.value);
    state.uncertain = state.uncertain.filter(id => state.ids.includes(id));
    saveScentSelection(state);
    const button = multi.querySelector('[data-pw-multi-add]');
    if (button) setMessage(button, '');
    restoreScentSelection();
  }, true);

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-pw-second-room-add], [data-pw-cart-smart-add]');
    if (!button || button.disabled) return;

    const choice = button.closest('[data-pw-product-upsell]')?.querySelector('[data-pw-upsell-select]');
    const variantId = choice ? choice.value : button.getAttribute('data-variant-id');
    const multi = button.closest('[data-pw-multi-fragrance]');
    const selected = multi ? Array.from(multi.querySelectorAll('[data-pw-scent-choice]:checked:not(:disabled)')) : [];
    if (multi ? !selected.length : !variantId) return;
    if (multi) multi.querySelectorAll('[data-pw-scent-choice]').forEach(input => { input.disabled = true; });

    const pair = button.closest('[data-pw-pair-product]');
    if (pair?.dataset.pwPending === 'true') return;
    if (pair) pair.dataset.pwPending = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.dataset.pwPending = 'true';
    if (choice) choice.disabled = true;
    setMessage(button, '');

    let addSucceeded = false;

    try {
      const root = getRoot();

      // This snippet is rendered on two different surfaces: the cart drawer (always present in
      // the DOM via the overlay-group, on every page) and the full /cart page's own line-item
      // list (sections/main-cart.liquid). The drawer already refreshes itself in place via the
      // theme's native cart:change listener. The cart page has no such listener for its own
      // section, so -- to avoid ever reloading or navigating away from the drawer experience --
      // we ask Shopify to re-render whichever section actually contains this button and swap it
      // in place ourselves, the same way the theme does it internally.
      const inDrawer = !!button.closest('cart-drawer');
      const productUpsell = !!button.closest('[data-pw-product-upsell]');
      const drawer = document.querySelector('cart-drawer');
      const drawerSectionId = inDrawer ? closestSectionId(button) : (productUpsell && drawer ? closestSectionId(drawer) : null);
      const ownSectionId = inDrawer || productUpsell ? null : closestSectionId(button);

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));
      if (ownSectionId && !sectionsToBundle.includes(ownSectionId)) {
        sectionsToBundle.push(ownSectionId);
      }

      const itemsToAdd = multi ? selected.map(input => ({ id: Number(input.value), quantity: 1 })) : [{ id: Number(variantId), quantity: 1 }];
      if (pair) {
        const form = button.closest('.product-info')?.querySelector('form[is="product-form"]');
        if (!form) throw new Error('Product form not found');
        const selection = new FormData(form);
        const scentVariant = Number(selection.get('id'));
        const requestedQuantity = Number(selection.get('quantity') || 1);
        if (!Number.isSafeInteger(scentVariant) || scentVariant <= 0 || !Number.isSafeInteger(requestedQuantity) || requestedQuantity < 1) throw new Error('Invalid product selection');
        const beforeResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!beforeResponse.ok) throw new Error('Unable to read cart');
        const before = await beforeResponse.json();
        const properties = {};
        for (const [key, value] of selection.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        const sellingPlan = selection.get('selling_plan');
        const alreadyInCart = before.items.filter(item => Number(item.variant_id) === scentVariant &&
          String(item.selling_plan_allocation?.selling_plan?.id || '') === String(sellingPlan || '') &&
          Object.keys({ ...properties, ...(item.properties || {}) }).every(key => String(properties[key] || '') === String(item.properties?.[key] || ''))
        ).reduce((sum, item) => sum + Number(item.quantity), 0);
        const bundleVariant = Number(button.getAttribute('data-pw-bundle-variant') || 0);
        if (bundleVariant) itemsToAdd[0] = { id: bundleVariant, quantity: requestedQuantity };
        const missing = bundleVariant ? 0 : Math.max(0, requestedQuantity - alreadyInCart);
        if (missing) {
          const scentItem = { id: scentVariant, quantity: missing, properties };
          if (sellingPlan) scentItem.selling_plan = Number(sellingPlan);
          itemsToAdd.push(scentItem);
        }
      }
      if (drawerSectionId && !sectionsToBundle.includes(drawerSectionId)) sectionsToBundle.push(drawerSectionId);
      if (multi) {
        const state = readScentSelection();
        state.uncertain = [...new Set([...state.uncertain, ...selected.map(input => input.value)])];
        saveScentSelection(state);
      }
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: itemsToAdd,
          sections: sectionsToBundle,
          sections_url: window.location.pathname + window.location.search
        })
      });

      if (!response.ok) throw new Error('Unable to update cart');
      const addResult = await response.json();
      addSucceeded = true;
      if (multi) {
        const state = readScentSelection();
        const added = new Set(selected.map(input => input.value));
        state.ids = state.ids.filter(id => !added.has(id));
        state.uncertain = state.uncertain.filter(id => !added.has(id));
        saveScentSelection(state);
        selected.forEach(input => { input.checked = false; });
      }
      setMessage(button, button.getAttribute('data-pw-success-message') || '');

      // Read Shopify's authoritative cart, including automatic discount allocations.
      let cart = null;
      try {
        const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (error) {
        cart = null;
      }
      // On the full /cart page, reload after a confirmed add. This guarantees that the line,
      // subtotal, discount and next applicable upsell are visible even if Shopify returns
      // stale or incomplete section HTML. The drawer keeps its faster in-place update below.
      if (!inDrawer && !productUpsell) {
        window.location.reload();
        return;
      }

      // Attach the freshly rendered section HTML, then hand it to the theme's native
      // cart:change handler so the drawer and cart-count badge update in place.

      const renderedSections = addResult && addResult.sections;
      const requiredSectionId = inDrawer || productUpsell ? drawerSectionId : ownSectionId;
      const requiredMarkup = requiredSectionId && renderedSections && renderedSections[requiredSectionId];
      const hasRequiredSection = typeof requiredMarkup === 'string' && requiredMarkup.trim() !== '';

      if (cart && hasRequiredSection) {
        cart.sections = addResult.sections;
        if (productUpsell) {
          button.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: addResult.items || [addResult], cart } }));
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        }));

        if (pair || multi) document.querySelector('cart-drawer')?.show();

        // Surfaces with no native cart:change listener of their own (e.g. the /cart page's own
        // line-item table) get swapped in place here: replace the whole rendered section with
        // the fresh server-rendered markup, exactly like the drawer does for itself internally.
        if (ownSectionId) {
          const freshMarkup = addResult.sections[ownSectionId];
          const freshDoc = freshMarkup ? new DOMParser().parseFromString(freshMarkup, 'text/html') : null;
          const freshSection = freshDoc && freshDoc.querySelector('.shopify-section');
          const currentSection = document.getElementById(`shopify-section-${ownSectionId}`);

          if (freshSection && currentSection) {
            currentSection.replaceWith(freshSection);
          } else {
            // A cart page must never look unchanged after a successful upsell. Shopify can
            // occasionally omit a requested section from an async response, so reload only
            // in that exceptional case; normally the section is updated in place.
            window.location.reload();
            return;
          }
        }
      } else {
        // Safety net (should not happen): no rendered sections came back, so ask the theme
        // to re-fetch and re-render the drawer itself.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        fetch(getRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(response => response.ok ? response.json() : Promise.reject())
          .then(freshCart => document.documentElement.dispatchEvent(new CustomEvent('pw:cart-reconciled', { detail: { cart: freshCart } })))
          .catch(() => {});
        if (pair || multi) document.querySelector('cart-drawer')?.show();
      }
    } catch (error) {
      if (addSucceeded) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        fetch(getRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(response => response.ok ? response.json() : Promise.reject())
          .then(freshCart => document.documentElement.dispatchEvent(new CustomEvent('pw:cart-reconciled', { detail: { cart: freshCart } })))
          .catch(() => {});
        if (pair || multi) document.querySelector('cart-drawer')?.show();
        return;
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
      setMessage(button, button.getAttribute('data-pw-error-message') || 'Please try again.');
    } finally {
      if (multi) {
        // A lost response may follow an accepted add: require a cart check/reload, never blind retry.
        multi.querySelectorAll('[data-pw-scent-choice]').forEach(input => { input.disabled = !addSucceeded; });
        if (addSucceeded) selected.forEach(input => { input.checked = false; });
        const count = multi.querySelectorAll('[data-pw-scent-choice]:checked').length;
        multi.querySelector('[data-pw-multi-count]').textContent = count;
        button.disabled = !addSucceeded || count === 0;
      }
      if (pair) delete pair.dataset.pwPending;
      delete button.dataset.pwPending;
      button.removeAttribute('aria-busy');
      if (choice) {
        choice.disabled = false;
        if (addSucceeded) choice.value = '';
        button.disabled = !choice.value;
      }
    }
  });
})();


// Shared product CTA pricing; loaded once with this deferred, cacheable asset.
(function() {
      if (window.PipWellsProductCtaPriceLoaded) return;
      window.PipWellsProductCtaPriceLoaded = true;

      function formatPrice(cents, locale) {
        var localeMap = { nl: 'nl-NL', de: 'de-DE', fr: 'fr-FR', es: 'es-ES', en: 'en-GB' };
        return new Intl.NumberFormat(localeMap[locale] || 'en-GB', {
          style: 'currency',
          currency: window.Shopify.currency.active,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cents / 100);
      }

      function discountedSelectionTotal(unitPrice, quantity, cartGroupQuantity, minimum, basisPoints, enabled) {
        var gross = unitPrice * quantity;
        // Eligible one-time purchases in the cart count towards the group threshold.
        if (!enabled || quantity + cartGroupQuantity < minimum) return gross;
        // Native Shopify rounds each unit discount down: 2x3695 ->6652 cents.
        return gross - Math.floor(unitPrice * basisPoints / 10000) * quantity;
      }

      function refreshButtonPrice(form) {
        if (!form) return;
        var button = form.querySelector('.pw-direct-checkout-button');
        var variantInput = form.querySelector('[name="id"]');
        var quantityInput = form.elements.namedItem('quantity');
        if (!button || !variantInput) return;

        var priceMap;
        try {
          priceMap = JSON.parse(button.getAttribute('data-pw-variant-prices') || '{}');
        } catch (error) {
          return;
        }

        var priceData = priceMap[String(variantInput.value)];
        if (!priceData) return;

        var quantity = Math.max(1, parseInt(quantityInput ? quantityInput.value : 1, 10) || 1);
        var priceElement = button.querySelector('.pw-direct-checkout-button__price');
        var comparePriceElement = button.querySelector('.pw-direct-checkout-button__compare-price');
        var locale = button.getAttribute('data-pw-price-locale');

        var sellingPlanInput = form.elements.namedItem('selling_plan');
        var discountEnabled = button.getAttribute('data-pw-discount-enabled') === 'true' && !(sellingPlanInput && sellingPlanInput.value);
        var groupQuantity = Math.max(0, Number(button.getAttribute('data-pw-cart-group-quantity')) || 0);
        if (button.getAttribute('data-pw-discount-secondary-ids')) {
          groupQuantity = Math.max(groupQuantity, Number(button.getAttribute('data-pw-cart-secondary-quantity')) || 0);
        }
        var minimum = Math.max(1, Number(button.getAttribute('data-pw-discount-minimum')) || 2);
        var basisPoints = Math.min(10000, Math.max(0, Number(button.getAttribute('data-pw-discount-bps')) || 0));
        var selectedTotal = discountedSelectionTotal(priceData.price, quantity, groupQuantity, minimum, basisPoints, discountEnabled);
        if (priceElement) priceElement.textContent = formatPrice(selectedTotal, locale);

        if (comparePriceElement) {
          var hasSetDiscount = selectedTotal < priceData.price * quantity;
          var showComparePrice = hasSetDiscount || priceData.compareAt > priceData.price;
          comparePriceElement.hidden = !showComparePrice;
          if (showComparePrice) comparePriceElement.textContent = formatPrice((hasSetDiscount ? priceData.price : priceData.compareAt) * quantity, locale);
        }
      }

      function refreshFromInput(input) {
        refreshButtonPrice(input.form || input.closest('form'));
      }

      document.addEventListener('variant:change', function() {
        document.querySelectorAll('.pw-direct-checkout-button').forEach(function(button) { refreshButtonPrice(button.closest('form')); });
      });
      function refreshCartPriceState(event) {
        var items = event.detail && event.detail.cart && event.detail.cart.items;
        if (!Array.isArray(items)) return;
        document.querySelectorAll('.pw-direct-checkout-button').forEach(function(button) {
          var ids = button.getAttribute('data-pw-discount-product-ids') || '';
          var count = items.reduce(function(sum, item) {
            return sum + (ids.indexOf('|' + item.product_id + '|') !== -1 && !item.selling_plan_allocation ? item.quantity : 0);
          }, 0);
          button.setAttribute('data-pw-cart-group-quantity', String(count));
          var secondaryIds = button.getAttribute('data-pw-discount-secondary-ids') || '';
          var secondaryCount = items.reduce(function(sum, item) {
            return sum + (secondaryIds.indexOf('|' + item.product_id + '|') !== -1 && !item.selling_plan_allocation ? item.quantity : 0);
          }, 0);
          button.setAttribute('data-pw-cart-secondary-quantity', String(secondaryCount));
          refreshButtonPrice(button.closest('form'));
        });
      }
      document.documentElement.addEventListener('cart:change', refreshCartPriceState);
      document.documentElement.addEventListener('pw:cart-reconciled', refreshCartPriceState);

      document.addEventListener('input', function(event) {
        if (event.target.matches('[name="quantity"]')) refreshFromInput(event.target);
      });

      document.addEventListener('change', function(event) {
        if (event.target.matches('[name="quantity"], [name="id"], [name="selling_plan"]')) refreshFromInput(event.target);
      });

      document.addEventListener('click', function(event) {
        var quantityButton = event.target.closest('.quantity-selector__button');
        if (!quantityButton) return;
        var quantityInput = quantityButton.parentElement && quantityButton.parentElement.querySelector('[name="quantity"]');
        if (quantityInput) window.setTimeout(function() { refreshFromInput(quantityInput); }, 0);
      });

      document.querySelectorAll('.pw-direct-checkout-button').forEach(function(button) {
        refreshButtonPrice(button.closest('form'));
      });
    })();

// Warm small shopping-choice images after the initial page has loaded.
(() => {
  if (window.PipWellsChoiceImageWarmup) return;
  window.PipWellsChoiceImageWarmup = true;
  const selector = '.pw-fragrance-selector img, .pw-fragrance-side-sets img, .pw-set-selector img, .pw-essence-colors img, .pw-diffuser-colors img, .pw-smart-upsell img';
  const seen = new WeakSet();
  const queue = [];
  let active = 0;
  let scheduled = false;
  let ready = document.readyState === 'complete';
  const allowed = () => {
    const connection = navigator.connection;
    return !document.hidden && !(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));
  };
  const visible = image => {
    const drawer = image.closest('cart-drawer');
    return image.isConnected && image.getClientRects().length > 0 && !image.closest('[hidden], [aria-hidden="true"]') && (!drawer || drawer.hasAttribute('open'));
  };
  function pump() {
    if (!allowed()) return;
    while (active < 2 && queue.length) {
      const image = queue.shift();
      if (!visible(image)) { seen.delete(image); continue; }
      if (image.complete && image.naturalWidth > 0) continue;
      active++;
      let finished = false;
      const done = () => {
        if (finished) return;
        finished = true;
        image.removeEventListener('load', done);
        image.removeEventListener('error', done);
        active--;
        schedule();
      };
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
      image.setAttribute('fetchpriority', 'low');
      image.loading = 'eager';
      if (image.complete && image.naturalWidth > 0) done();
    }
  }
  function collect() {
    document.querySelectorAll(selector).forEach(image => {
      if (seen.has(image) || image.loading !== 'lazy' || !visible(image)) return;
      seen.add(image);
      queue.push(image);
    });
    pump();
  }
  function schedule() {
    if (!ready || scheduled || !allowed()) return;
    scheduled = true;
    const run = () => { scheduled = false; if (allowed()) collect(); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 });
    else window.setTimeout(run, 500);
  }
  if (!ready) window.addEventListener('load', () => { ready = true; schedule(); }, { once: true });
  else schedule();
  ['cart:change', 'pw:cart-reconciled', 'product:rerender', 'dialog:after-show', 'visibilitychange'].forEach(name => document.addEventListener(name, schedule));
})();
